<template>
  <BaseTemplate app-name="Prestação de Contas" page-title="">
    <template #content>
      <div class="min-h-screen bg-slate-100 px-3 py-4 dark:bg-gray-900 sm:px-4 lg:px-6 print:bg-white print:p-0">
        <div class="mx-auto max-w-6xl space-y-4">

          <!-- ====== BARRA SUPERIOR ====== -->
          <header class="card flex flex-col gap-3 p-3 print:hidden sm:p-4 lg:flex-row lg:items-center lg:justify-between">
            <div class="flex items-center gap-3">
              <span class="rounded-lg bg-sky-100 p-2 dark:bg-sky-900/40">
                <ClipboardList class="h-5 w-5 text-sky-700 dark:text-sky-300" />
              </span>
              <div>
                <h1 class="text-base font-bold text-blue-900 dark:text-blue-200 sm:text-lg">Prestação de Contas CCDTI</h1>
                <p class="text-xs text-gray-500 dark:text-gray-400">Cada área preenche a sua parte · o relatório é montado com tudo junto</p>
              </div>
            </div>

            <div class="flex flex-wrap items-center gap-2 sm:gap-3">
              <label class="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                Competência
                <input v-model="competencia" type="month" class="input w-auto py-1.5 tabular-nums" aria-label="Competência" @change="trocarCompetencia" />
              </label>
              <div class="flex rounded-lg bg-slate-100 p-1 dark:bg-gray-900" role="tablist">
                <button type="button" role="tab" class="rounded-md px-3 py-1.5 text-sm font-medium transition" :class="vista !== 'relatorio' ? 'bg-white text-blue-900 shadow dark:bg-gray-700 dark:text-white' : 'text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200'" :aria-selected="vista !== 'relatorio'" @click="irPara('painel')">Painel</button>
                <button type="button" role="tab" class="rounded-md px-3 py-1.5 text-sm font-medium transition" :class="vista === 'relatorio' ? 'bg-white text-blue-900 shadow dark:bg-gray-700 dark:text-white' : 'text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200'" :aria-selected="vista === 'relatorio'" @click="irPara('relatorio')">Relatório</button>
              </div>
              <p class="min-w-[9rem] text-right text-xs" :class="estado.classe" aria-live="polite">{{ estado.texto }}</p>
            </div>
          </header>

          <!-- ====== PAINEL ====== -->
          <template v-if="vista === 'painel'">
            <section class="grid gap-5 lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]">
              <div>
                <h2 class="text-xl font-bold text-blue-900 dark:text-blue-200 sm:text-2xl">Competência {{ rotuloCompetencia(competencia) }}</h2>
                <p class="mt-2 max-w-[62ch] text-sm text-gray-600 dark:text-gray-300">
                  Cada área abre o cartão dela, preenche os campos e sai. O que for digitado fica salvo para todo mundo que abrir esta página.
                  Quando as áreas terminarem, a aba <b>Relatório</b> monta o documento com tudo junto, na ordem das seções.
                </p>
                <p class="mt-2 text-sm text-gray-700 dark:text-gray-200"><b>{{ prontas }} de {{ AREAS.length }}</b> partes com preenchimento avançado.</p>
                <div class="mt-3 h-2 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
                  <div class="h-full rounded-full bg-sky-500 transition-all duration-500" :style="{ width: progressoGeral + '%' }"></div>
                </div>
              </div>
              <ol class="card grid gap-2.5 p-4 text-sm text-gray-600 dark:text-gray-300">
                <li class="grid grid-cols-[1.4rem_1fr] gap-2"><span class="text-xs font-semibold text-sky-700 dark:text-sky-400">1</span><span><b class="text-gray-900 dark:text-gray-100">Escolha a sua área</b> no cartão correspondente.</span></li>
                <li class="grid grid-cols-[1.4rem_1fr] gap-2"><span class="text-xs font-semibold text-sky-700 dark:text-sky-400">2</span><span><b class="text-gray-900 dark:text-gray-100">Preencha os campos.</b> Tudo é salvo sozinho, campo a campo.</span></li>
                <li class="grid grid-cols-[1.4rem_1fr] gap-2"><span class="text-xs font-semibold text-sky-700 dark:text-sky-400">3</span><span><b class="text-gray-900 dark:text-gray-100">Confira no Relatório.</b> Dá para imprimir em PDF, gerar o Word no modelo oficial ou exportar os dados.</span></li>
              </ol>
            </section>

            <section class="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3" aria-label="Áreas">
              <button
                v-for="area in AREAS"
                :key="area.id"
                type="button"
                class="card grid gap-2 p-4 text-left transition hover:-translate-y-0.5 hover:border-sky-400 hover:shadow-md"
                @click="abrirArea(area.id)"
              >
                <div class="flex items-center justify-between gap-2">
                  <span class="text-[11px] font-semibold uppercase tracking-[0.1em] text-gray-500 dark:text-gray-400">{{ area.sec }}</span>
                  <span class="badge" :class="situacao(area).badge">{{ situacao(area).label }} · {{ progresso(area) }}%</span>
                </div>
                <span class="text-sm font-semibold leading-snug text-gray-900 dark:text-white">{{ area.titulo }}</span>
                <span class="text-xs text-gray-500 dark:text-gray-400">
                  {{ area.area }}
                  <template v-if="dados[area.id]?.responsavel"> · {{ dados[area.id].responsavel }}</template>
                  <template v-if="dados[area.id]?.atualizado"> · atualizado em {{ formatarData(dados[area.id].atualizado) }}</template>
                </span>
                <div class="h-1.5 w-full overflow-hidden rounded-full bg-slate-100 dark:bg-gray-700">
                  <div class="h-full rounded-full transition-all duration-500" :class="situacao(area).barra" :style="{ width: progresso(area) + '%' }"></div>
                </div>
              </button>
            </section>

            <!-- Histórico de envios -->
            <section v-if="auditoria.length" class="card p-4">
              <h3 class="text-sm font-semibold text-gray-900 dark:text-gray-100">Últimos envios desta competência</h3>
              <ul class="mt-2 divide-y divide-gray-100 text-xs text-gray-600 dark:divide-gray-700 dark:text-gray-300">
                <li v-for="(a, i) in auditoria" :key="i" class="flex flex-wrap justify-between gap-2 py-1.5">
                  <span><b class="text-gray-800 dark:text-gray-100">{{ nomeArea(a.area) }}</b> · {{ a.autor || 'sem identificação' }}</span>
                  <span class="tabular-nums text-gray-400">{{ formatarDataHora(a.quando) }}</span>
                </li>
              </ul>
            </section>
          </template>

          <!-- ====== FORMULÁRIO DE UMA ÁREA ====== -->
          <section v-else-if="vista === 'form' && areaAberta" class="card p-4 sm:p-6">
            <FormularioArea :area="areaAberta" :dados="dadosArea(areaAberta.id)" @alterado="agendarGravacao" @voltar="irPara('painel')" />
          </section>

          <!-- ====== RELATÓRIO ====== -->
          <template v-else>
            <div class="flex flex-wrap items-center gap-2 print:hidden">
              <button type="button" class="btn-primary" @click="imprimir"><Printer class="h-4 w-4" /> Imprimir ou salvar em PDF</button>
              <button type="button" class="btn-secondary" :disabled="exportando" @click="exportarDocx"><FileType class="h-4 w-4" /> Exportar Word (DOCX)</button>
              <button type="button" class="btn-secondary" :disabled="exportando" @click="exportarJson"><FileJson class="h-4 w-4" /> Exportar dados (JSON)</button>
              <button type="button" class="btn-secondary" @click="exportarMarkdown"><FileText class="h-4 w-4" /> Exportar texto (Markdown)</button>
              <span v-if="faltando.length" class="badge bg-amber-100 px-3 py-1 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
                {{ faltando.length }} {{ faltando.length === 1 ? 'parte ainda não foi enviada' : 'partes ainda não foram enviadas' }}: {{ secoesFaltando }}
              </span>
              <span v-else class="badge bg-emerald-100 px-3 py-1 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300">Todas as áreas responderam</span>
            </div>
            <RelatorioPrestacao :competencia="competencia" :dados="dados" />
          </template>
        </div>
      </div>
    </template>
  </BaseTemplate>
</template>

<script setup>
import { ref, reactive, computed, onMounted, onUnmounted, nextTick } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { ClipboardList, Printer, FileJson, FileText, FileType } from 'lucide-vue-next';
import BaseTemplate from '@/views/templates/BaseTemplate.vue';
import apiClient from '@/services/authService';
import FormularioArea from './FormularioArea.vue';
import RelatorioPrestacao from './RelatorioPrestacao.vue';
import { AREAS } from './areas';
import {
  competenciaAtual, rotuloCompetencia, areaVazia, findArea,
  progressoArea, situacaoArea, LIMIAR_PRONTO, formatarData, formatarDataHora,
  gerarMarkdown, baixarArquivo,
} from './prestacao';

const API = '/api/v1/prestacao-contas';
const DEBOUNCE_GRAVACAO_MS = 700;
const INTERVALO_ATUALIZACAO_MS = 60000;

const route = useRoute();
const router = useRouter();

// ---------------------------------------------------------------------------
// Estado
// ---------------------------------------------------------------------------
const competencia = ref(String(route.query.competencia || '').match(/^\d{4}-\d{2}$/) ? route.query.competencia : competenciaAtual());
const vista = ref('painel');                 // painel | form | relatorio
const areaAbertaId = ref(null);
const dados = reactive({});                  // { areaId: { responsavel, atualizado, campos } }
const auditoria = ref([]);
const estado = reactive({ texto: 'carregando…', classe: 'text-gray-500 dark:text-gray-400' });
const exportando = ref(false);
const pendentes = new Map();                 // areaId -> timeout de gravação
let intervaloAtualizacao = null;

const areaAberta = computed(() => (areaAbertaId.value ? findArea(areaAbertaId.value) : null));
const chaveLocal = () => `prestacao-contas:${competencia.value}`;

const agoraTexto = () => new Date().toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
const setEstado = (texto, tipo = 'ok') => {
  estado.texto = texto;
  estado.classe = tipo === 'erro' ? 'text-red-600 dark:text-red-400' : tipo === 'aviso' ? 'text-amber-600 dark:text-amber-400' : 'text-gray-500 dark:text-gray-400';
};

// ---------------------------------------------------------------------------
// Progresso
// ---------------------------------------------------------------------------
const progresso = (area) => progressoArea(area, dados[area.id]);
const situacao = (area) => situacaoArea(progresso(area));
const prontas = computed(() => AREAS.filter((a) => progresso(a) >= LIMIAR_PRONTO).length);
const progressoGeral = computed(() => Math.round(AREAS.reduce((t, a) => t + progresso(a), 0) / AREAS.length));
const faltando = computed(() => AREAS.filter((a) => progresso(a) === 0));
const secoesFaltando = computed(() => [...new Set(faltando.value.map((a) => a.sec))].join(', '));
const nomeArea = (id) => findArea(id)?.titulo || id;

function dadosArea(id) {
  if (!dados[id]) dados[id] = areaVazia();
  if (!dados[id].campos) dados[id].campos = {};
  return dados[id];
}

// ---------------------------------------------------------------------------
// Persistência: API do sistema com cópia local por competência (funciona offline)
// ---------------------------------------------------------------------------
function substituirDados(novos) {
  Object.keys(dados).forEach((k) => delete dados[k]);
  Object.entries(novos || {}).forEach(([k, v]) => { dados[k] = { ...areaVazia(), ...v, campos: v.campos || {} }; });
}

function salvarLocal() {
  try { localStorage.setItem(chaveLocal(), JSON.stringify(dados)); } catch (e) { /* armazenamento indisponível */ }
}

async function carregar() {
  try {
    const bruto = localStorage.getItem(chaveLocal());
    substituirDados(bruto ? JSON.parse(bruto) : {});
  } catch (e) { substituirDados({}); }

  try {
    const { data } = await apiClient.get(`${API}/${competencia.value}/`);
    substituirDados(data.areas);
    salvarLocal();
    setEstado(`sincronizado · ${agoraTexto()}`);
  } catch (e) {
    setEstado('servidor fora do ar · salvo neste dispositivo', 'aviso');
  }
  carregarAuditoria();
}

async function carregarAuditoria() {
  try {
    const { data } = await apiClient.get(`${API}/${competencia.value}/auditoria/`, { params: { limite: 12 } });
    auditoria.value = data;
  } catch (e) { auditoria.value = []; }
}

async function gravarAgora(areaId) {
  pendentes.delete(areaId);
  const d = dadosArea(areaId);
  d.atualizado = new Date().toISOString();
  salvarLocal();
  try {
    const { data } = await apiClient.put(`${API}/${competencia.value}/${areaId}/`, { responsavel: d.responsavel, campos: d.campos });
    if (data?.atualizado) d.atualizado = data.atualizado;
    setEstado(`salvo · ${agoraTexto()}`);
  } catch (e) {
    setEstado('não deu para salvar no servidor · tente de novo', 'erro');
  }
}

// Cada campo é salvo sozinho, com um pequeno atraso para agrupar a digitação
function agendarGravacao(areaId) {
  salvarLocal();
  setEstado('salvando…');
  clearTimeout(pendentes.get(areaId));
  pendentes.set(areaId, setTimeout(() => gravarAgora(areaId), DEBOUNCE_GRAVACAO_MS));
}

// Ao sair da página (ou fechar a aba) envia o que ainda estava aguardando o debounce
function descarregarPendentes() {
  for (const [areaId, timeout] of pendentes) {
    clearTimeout(timeout);
    gravarAgora(areaId);
  }
}

// Atualiza o painel de tempos em tempos para ver o que as outras áreas enviaram
function iniciarAtualizacaoPeriodica() {
  intervaloAtualizacao = setInterval(async () => {
    if (document.hidden || vista.value === 'form' || pendentes.size) return;
    try {
      const { data } = await apiClient.get(`${API}/${competencia.value}/`);
      substituirDados(data.areas);
      salvarLocal();
      carregarAuditoria();
    } catch (e) { /* mantém o que está na tela */ }
  }, INTERVALO_ATUALIZACAO_MS);
}

// ---------------------------------------------------------------------------
// Navegação interna
// ---------------------------------------------------------------------------
function irPara(destino) {
  vista.value = destino;
  if (destino !== 'form') areaAbertaId.value = null;
  window.scrollTo({ top: 0 });
}
function abrirArea(id) {
  dadosArea(id);
  areaAbertaId.value = id;
  vista.value = 'form';
  window.scrollTo({ top: 0 });
}
function trocarCompetencia() {
  if (!competencia.value) competencia.value = competenciaAtual();
  router.replace({ query: { ...route.query, competencia: competencia.value } });
  irPara('painel');
  carregar();
}

// ---------------------------------------------------------------------------
// Exportações (as mesmas do formulário original: PDF pela impressão, JSON e Markdown)
// ---------------------------------------------------------------------------
async function imprimir() {
  await nextTick();
  window.print();
}

// Word: o backend preenche o modelo oficial (.dotx) com os valores das áreas
async function exportarDocx() {
  exportando.value = true;
  try {
    const { data } = await apiClient.get(`${API}/${competencia.value}/export.docx`, { responseType: 'blob' });
    baixarArquivo(`Relatorio-Gestao-CCDTI-${competencia.value}.docx`, data);
  } catch (e) {
    setEstado('não foi possível gerar o Word', 'erro');
  } finally {
    exportando.value = false;
  }
}

async function exportarJson() {
  exportando.value = true;
  try {
    const { data } = await apiClient.get(`${API}/${competencia.value}/export.json`, { responseType: 'blob' });
    baixarArquivo(`prestacao-contas-${competencia.value}.json`, data);
  } catch (e) {
    setEstado('não foi possível exportar o JSON', 'erro');
  } finally {
    exportando.value = false;
  }
}

function exportarMarkdown() {
  baixarArquivo(`relatorio-${competencia.value}.md`, gerarMarkdown(competencia.value, dados), 'text/markdown;charset=utf-8');
}

// ---------------------------------------------------------------------------
// Ciclo de vida
// ---------------------------------------------------------------------------
onMounted(() => {
  carregar();
  iniciarAtualizacaoPeriodica();
  window.addEventListener('beforeunload', descarregarPendentes);
});
onUnmounted(() => {
  if (intervaloAtualizacao) clearInterval(intervaloAtualizacao);
  window.removeEventListener('beforeunload', descarregarPendentes);
  descarregarPendentes();
});
</script>
